import { AppLink } from "../components/AppLink";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Icon } from "../components/Icon";
import { NotFoundContent } from "../components/NotFoundContent";
import { Picture } from "../components/Picture";
import { ArticleCard } from "../components/journal/ArticleCard";
import {
  articleBodies,
  countWords,
  headingId,
  type ArticleBlock,
  type ListItem,
} from "../data/articleBodies";
import {
  articleImageUrl,
  articles,
  formatDate,
  getArticle,
  type Article as ArticleData,
} from "../data/articles";
import { images } from "../generated/images";
import type { PageProps } from "../routes";
import { Seo, breadcrumbLd } from "../lib/seo";
import { SITE, absoluteUrl } from "../lib/site";
import { btnOutline, btnPrimary, container } from "../lib/ui";

function Item({ item }: { item: ListItem }) {
  if (typeof item === "string") return <li>{item}</li>;
  return (
    <li>
      <strong>{item.term}</strong> {item.text}
    </li>
  );
}

function Block({ block }: { block: ArticleBlock }) {
  switch (block.type) {
    case "p":
      return <p>{block.text}</p>;
    case "h2":
      return (
        <h2 id={headingId(block.text)} className="scroll-mt-28">
          {block.text}
        </h2>
      );
    case "h3":
      return <h3>{block.text}</h3>;
    case "ul":
      return (
        <ul>
          {block.items.map((item, i) => (
            <Item key={i} item={item} />
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol>
          {block.items.map((item, i) => (
            <Item key={i} item={item} />
          ))}
        </ol>
      );
    case "quote":
      return (
        <blockquote>
          <p>{block.text}</p>
        </blockquote>
      );
  }
}

/** Up to three other essays: same category first, then the most recent. */
function related(a: ArticleData) {
  const others = articles.filter((o) => o.slug !== a.slug);
  const same = others.filter((o) => o.category === a.category);
  const rest = others.filter((o) => o.category !== a.category);
  return [...same, ...rest].slice(0, 3);
}

function ShareLinks({
  title,
  url,
  className = "",
}: {
  title: string;
  url: string;
  className?: string;
}) {
  const linkClass =
    "w-10 h-10 rounded-full border border-gold-dark/40 text-primary hover:bg-primary hover:text-gold-light flex items-center justify-center transition-colors";
  return (
    <div className={`flex items-center gap-space-sm ${className}`}>
      <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant">
        Share
      </span>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on WhatsApp (opens in a new tab)"
        className={linkClass}
      >
        <Icon name="chat" size={18} />
      </a>
      <a
        href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`I thought you might like this essay from ${SITE.name}:\n\n${url}`)}`}
        aria-label="Share by email"
        className={linkClass}
      >
        <Icon name="mail" size={18} />
      </a>
    </div>
  );
}

export default function Article({ params }: PageProps) {
  const a = params.slug ? getArticle(params.slug) : undefined;
  const body = a ? articleBodies[a.slug] : undefined;
  if (!a || !body) return <NotFoundContent />;

  const path = `/journal/${a.slug}`;
  const url = absoluteUrl(path);
  const img = images[a.image];
  const imagePath = `/images/${a.image}-${img.widths[img.widths.length - 1]}.jpg`;
  const modified = a.updated ?? a.date;
  const toc = body.filter(
    (b): b is Extract<ArticleBlock, { type: "h2" }> => b.type === "h2",
  );
  const trail: Array<[string, string]> = [
    ["Journal", "/journal"],
    [a.shortTitle, path],
  ];
  const seo = a.seo ?? {
    title: a.shortTitle,
    description: a.excerpt,
    keywords: [],
  };

  const jsonLd = [
    breadcrumbLd(trail),
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${url}#article`,
      headline: a.title,
      description: seo.description,
      image: [articleImageUrl(a)],
      datePublished: a.date,
      dateModified: modified,
      author: {
        "@type": "Person",
        name: a.author.name,
        jobTitle: a.author.role,
      },
      publisher: { "@id": `${SITE.origin}/#business` },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      articleSection: a.category,
      keywords: seo.keywords.join(", "),
      wordCount: countWords(body),
      inLanguage: "en-IN",
      isPartOf: { "@id": `${absoluteUrl("/journal")}#blog` },
    },
  ];

  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={path}
        image={imagePath}
        type="article"
        article={{
          publishedTime: a.date,
          modifiedTime: modified,
          author: a.author.name,
          section: a.category,
        }}
        jsonLd={jsonLd}
      />

      <article className="bg-surface">
        {/* HEADER */}
        <header className="relative overflow-hidden">
          <div className="absolute -top-40 right-10 w-96 h-96 rounded-full bg-gold-light/20 blur-3xl pointer-events-none" />
          <div className="absolute top-20 left-1/4 w-80 h-80 rounded-full bg-emerald-vivid/10 blur-3xl pointer-events-none" />
          <div className={`relative ${container} pt-10 md:pt-12 pb-space-lg`}>
            <div className="max-w-6xl mx-auto">
              <Breadcrumbs trail={trail} className="mb-space-lg" />
              <div className="max-w-4xl">
                <div className="flex flex-wrap items-center gap-x-space-sm gap-y-1 text-secondary font-label-sm text-label-sm uppercase tracking-widest mb-space-md">
                  <span>{a.category}</span>
                  <span aria-hidden="true">•</span>
                  <time dateTime={a.date}>{formatDate(a.date)}</time>
                  <span aria-hidden="true">•</span>
                  <span className="flex items-center gap-1">
                    <Icon name="schedule" size={13} /> {a.readMinutes} min read
                  </span>
                </div>
                <h1 className="font-headline-lg text-headline-lg-mobile md:text-headline-xl text-primary tracking-tight !leading-[1.15] animate-rise">
                  {a.title}
                </h1>
                <p className="font-body-xl text-body-lg md:text-body-xl text-on-surface-variant font-light leading-relaxed mt-space-md max-w-3xl animate-rise [animation-delay:80ms]">
                  {a.excerpt}
                </p>
                <div className="flex flex-wrap items-center justify-between gap-space-md mt-space-lg pt-space-md border-t border-surface-container-highest">
                  <div className="flex items-center gap-space-sm">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-primary-container shrink-0">
                      <Picture
                        name="journal-author-portrait"
                        alt={`Portrait of ${a.author.name}`}
                        sizes="48px"
                        className="block w-full h-full"
                        imgClassName="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-body-sm text-body-sm font-semibold text-on-surface">
                        {a.author.name}
                      </p>
                      <p className="font-label-sm text-label-sm text-outline uppercase tracking-wider">
                        {a.author.role}
                      </p>
                    </div>
                  </div>
                  <ShareLinks title={a.title} url={url} />
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* HERO IMAGE */}
        <div className={`${container} pb-space-xl`}>
          <div className="relative rounded-xl overflow-hidden shadow-xl bg-surface-dim aspect-[16/10] md:aspect-[21/9] max-w-6xl mx-auto">
            <Picture
              name={a.image}
              alt={a.imageAlt}
              sizes="(min-width: 1200px) 1152px, 100vw"
              priority
              className="absolute inset-0 block w-full h-full"
              imgClassName="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 bg-botanical-dark/80 backdrop-blur-sm text-gold-light text-label-sm font-label-sm uppercase px-2.5 py-1 rounded">
              {a.topic}
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className={`${container} pb-space-xl md:pb-20`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter max-w-6xl mx-auto">
            <aside className="hidden lg:block lg:col-span-3">
              <nav
                aria-label="In this essay"
                className="sticky top-28 flex flex-col gap-space-sm"
              >
                <p className="font-label-sm text-label-sm uppercase tracking-widest text-secondary">
                  In this essay
                </p>
                <ol className="flex flex-col gap-2 border-l border-surface-container-highest">
                  {toc.map((h) => (
                    <li key={h.text}>
                      <a
                        href={`#${headingId(h.text)}`}
                        className="block -ml-px pl-space-sm border-l border-transparent hover:border-secondary font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors"
                      >
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            </aside>
            <div className="lg:col-span-8 lg:col-start-5 max-w-[720px]">
              <div className="prose-article">
                {body.map((b, i) => (
                  <Block key={i} block={b} />
                ))}
              </div>
              <div className="mt-space-xl pt-space-md border-t border-surface-container-highest flex flex-wrap items-center justify-between gap-space-md">
                <AppLink
                  href="/journal"
                  className="inline-flex items-center gap-1 font-label-md text-label-md uppercase tracking-wider text-primary hover:text-secondary transition-colors"
                >
                  <Icon name="arrow_back" size={16} /> All essays
                </AppLink>
                <ShareLinks title={a.title} url={url} />
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* RELATED */}
      <section
        className="bg-surface-cream py-space-xl md:py-20"
        aria-labelledby="further-reading"
      >
        <div className={container}>
          <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-space-md mb-space-lg">
            <div>
              <span className="font-label-md text-label-md uppercase tracking-[0.2em] text-secondary">
                The Atelier Journal
              </span>
              <h2
                id="further-reading"
                className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-primary mt-space-xs"
              >
                Further Reading
              </h2>
            </div>
            <AppLink
              href="/journal"
              className="font-label-md text-label-md uppercase tracking-wider text-primary hover:text-secondary flex items-center gap-1 transition-colors"
            >
              <span>Read All Essays</span>
              <Icon name="arrow_forward" size={16} />
            </AppLink>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
            {related(a).map((r) => (
              <ArticleCard key={r.slug} article={r} className="reveal" />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-botanical-dark text-on-primary py-space-xl md:py-20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-container/30 rounded-full blur-3xl pointer-events-none" />
        <div
          className={`relative ${container} flex flex-col lg:flex-row lg:items-center justify-between gap-space-lg`}
        >
          <div className="reveal max-w-2xl">
            <span className="font-label-md text-label-md uppercase tracking-[0.25em] text-gold-light">
              Tailored Engagement
            </span>
            <h2 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-surface-ivory mt-space-xs">
              Planning a home in Nagercoil, Kanyakumari or Kerala?
            </h2>
            <p className="font-body-lg text-body-lg text-surface-ivory/75 mt-space-sm">
              Talk to our studio about your space. We will listen to how you
              live, look at your plans and suggest where to begin — with no
              obligation.
            </p>
          </div>
          <div className="reveal flex flex-wrap gap-space-md">
            <AppLink
              href="/contact"
              className={`${btnPrimary} !bg-gold-light !text-primary hover:!bg-surface-ivory`}
            >
              Book a Consultation <Icon name="arrow_forward" size={18} />
            </AppLink>
            <a
              href={SITE.whatsappUrl}
              target="_blank"
              rel="noopener"
              className={`${btnOutline} !border-gold-light/50 !text-surface-ivory hover:!bg-primary-container`}
            >
              <Icon name="chat" size={18} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
